import { ref } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../../../consts";
import { usePaginatedFetcher } from "@/composables/usePaginatedFetcher";
import { useVideoPlayback } from "@/composables/useVideoPlayback";

export function usePodcastVideos(token) {
  const toast = useToast();
  const { currentlyPlayingId, setPlaying, copyLink } = useVideoPlayback();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const shows = ref([]);
  const showsLoading = ref(false);
  const selectedShowIds = ref([]);
  const videoIdFilter = ref(null);
  const minDuration = ref(null);
  const maxDuration = ref(null);
  const dateRange = ref(null);

  const fetchShows = async () => {
    showsLoading.value = true;
    try {
      const response = await axios.get(`${API_BASE_URL}/podcaster/shows/all`, {
        ...authHeaders,
        params: { fields: "show_id,show_title" },
      });
      shows.value = (response.data?.podcastShows || [])
        .filter((show) => show.show_title)
        .sort((a, b) => a.show_title.localeCompare(b.show_title));
    } catch (error) {
      console.error("Error fetching shows for video filter:", error);
    } finally {
      showsLoading.value = false;
    }
  };

  const buildExtraParams = () => {
    const showIds = selectedShowIds.value || [];
    const [startDate, endDate] = dateRange.value || [];
    return {
      ...(showIds.length ? { showIds: showIds.join(",") } : {}),
      ...(videoIdFilter.value ? { videoId: videoIdFilter.value } : {}),
      ...(minDuration.value != null ? { minDuration: minDuration.value } : {}),
      ...(maxDuration.value != null ? { maxDuration: maxDuration.value } : {}),
      ...(startDate ? { startDate: startDate.toISOString().slice(0, 10) } : {}),
      ...(endDate ? { endDate: endDate.toISOString().slice(0, 10) } : {}),
    };
  };

  const fetchVideosPage = async (params) => {
    const response = await axios.get(`${API_BASE_URL}/podcast-videos`, {
      ...authHeaders,
      params,
    });
    return {
      items: response.data?.data?.videos || [],
      total: response.data?.data?.total || 0,
    };
  };

  const {
    items: videos,
    total: totalVideos,
    page,
    rows,
    firstRow,
    loading,
    fetchPage: fetchVideos,
    refresh: refreshVideos,
    onPageChange,
  } = usePaginatedFetcher(fetchVideosPage, {
    getExtraParams: buildExtraParams,
    initialRows: 20,
  });

  const applyFilters = () => {
    page.value = 0;
    fetchVideos(0);
  };

  const clearVideoIdFilter = () => {
    videoIdFilter.value = null;
    applyFilters();
  };

  const deletingVideoId = ref(null);

  const deleteVideo = async (video) => {
    deletingVideoId.value = video.video_id;
    try {
      await axios.delete(
        `${API_BASE_URL}/podcast-videos/${video.video_id}`,
        authHeaders
      );
      toast.add({
        severity: "success",
        summary: "Deleted",
        detail: "Video removed.",
        life: 4000,
      });
      refreshVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete video.",
        life: 4000,
      });
    } finally {
      deletingVideoId.value = null;
    }
  };

  return {
    shows,
    showsLoading,
    selectedShowIds,
    fetchShows,
    videos,
    totalVideos,
    page,
    rows,
    firstRow,
    loading,
    fetchVideos,
    refreshVideos,
    onPageChange,
    applyFilters,
    deletingVideoId,
    deleteVideo,
    currentlyPlayingId,
    setPlaying,
    copyLink,
    videoIdFilter,
    clearVideoIdFilter,
    minDuration,
    maxDuration,
    dateRange,
  };
}
